import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CrossDocumentsTemplateConfig } from '../template/MasterDocumentTemplate.types';
import type {
  AvailableSourceDocument,
  CrossedSourceDocument,
  CrossingDocumentType,
} from '../MasterDocument.types';
import { resolveCrossingError } from './crossingFetch';
import { useMasterDocumentTranslation } from '../i18n';
import type { CrossingFetch } from './crossingFetch';

// ─── Rutas canónicas (override vía template.crossDocuments.routes) ──
// El consumidor normalmente remapea estas rutas a su backend vía
// `adapter.genericFetch`. `{id}` se sustituye por el documentId.
const DEFAULT_ROUTES = {
  available: '/api/v1/crossings/available-source-documents?document_id={id}',
  crossed: '/api/v1/crossings/{id}/source-documents',
  cross: '/api/v1/crossings/{id}/cross',
  uncross: '/api/v1/crossings/{id}/uncross',
} as const;

function buildRoute(
  template: string,
  documentId: string,
): string {
  return template.replace('{id}', encodeURIComponent(documentId));
}

// ─── Normalización del sign (acepta number/string del backend) ─────
function normalizeSign(raw: unknown): 1 | -1 {
  const n = typeof raw === 'string' ? Number(raw) : (raw as number);
  return Number.isFinite(n) && n < 0 ? -1 : 1;
}

function deriveType(
  rawType: unknown,
  sign: 1 | -1,
): CrossingDocumentType {
  if (typeof rawType === 'string') {
    const upper = rawType.toUpperCase();
    if (upper === 'ECD' || upper === 'DCD') return upper;
  }
  return sign < 0 ? 'DCD' : 'ECD';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapAvailable(raw: any): AvailableSourceDocument {
  const crossingSign = normalizeSign(raw.crossing_sign ?? raw.crossingSign);
  const lineCountRaw = raw.line_count ?? raw.lineCount;
  return {
    id: String(raw.id ?? raw.document_id ?? raw.documentId),
    number: String(raw.prefix_number ?? raw.number ?? raw.document_number ?? raw.documentNumber ?? ''),
    type: deriveType(raw.type ?? raw.parent_class_code ?? raw.parentClassCode, crossingSign),
    classLabel: String(raw.class_label ?? raw.classLabel ?? raw.parent_class_code ?? raw.parentClassCode ?? ''),
    crossingSign,
    netTotal: Number(raw.net_total ?? raw.netTotal ?? 0),
    date: raw.date ?? raw.document_date ?? raw.documentDate ?? undefined,
    lineCount: lineCountRaw != null ? Number(lineCountRaw) : undefined,
    state: raw.state ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapCrossed(raw: any): CrossedSourceDocument {
  const crossingSign = normalizeSign(raw.crossing_sign ?? raw.crossingSign);
  return {
    id: String(raw.id ?? raw.document_id ?? raw.documentId),
    number: String(raw.prefix_number ?? raw.number ?? raw.document_number ?? raw.documentNumber ?? ''),
    type: deriveType(raw.type ?? raw.parent_class_code ?? raw.parentClassCode, crossingSign),
    classLabel: String(raw.class_label ?? raw.classLabel ?? raw.parent_class_code ?? raw.parentClassCode ?? ''),
    crossingSign,
    netTotal: Number(raw.net_total ?? raw.netTotal ?? 0),
    state: raw.state ?? undefined,
    date: raw.date ?? raw.document_date ?? raw.documentDate ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toArray(payload: any, key: string): any[] {
  if (Array.isArray(payload)) return payload;
  return payload?.[key] ?? payload?.data ?? payload?.items ?? [];
}

interface UseCrossDocumentsOptions {
  config: CrossDocumentsTemplateConfig | undefined;
  documentId: string | undefined;
  docState: string;
  /** Salida HTTP (enruta por `adapter.genericFetch`). Ver {@link createCrossingFetch}. */
  crossingFetch: CrossingFetch;
  /**
   * ¿El usuario tiene el permiso (`requiresPermission`)? El kit no resuelve RBAC;
   * el consumidor inyecta este booleano. Si se omite, se asume `true`.
   */
  canCross?: boolean;
  /** Notifica al consumidor tras un cross/uncross exitoso (para invalidar detail/totals/FactBox). */
  onCrossingChanged?: () => void;
}

export interface CrossDocumentsState {
  available: AvailableSourceDocument[];
  crossed: CrossedSourceDocument[];
  selectedIds: string[];
  /** `Σ(ECDs.net) − Σ(DCDs.net)` de la selección actual. */
  subtotal: number;
  /** Subtotal consolidado de los documentos ya cruzados. */
  crossedSubtotal: number;
  loading: boolean;
  busy: boolean;
  /** Editable = estado en `crossOn` Y permiso concedido. */
  editable: boolean;
  error: string | undefined;
}

export interface CrossDocumentsActions {
  toggleSelect: (id: string, selected: boolean) => void;
  cross: () => Promise<void>;
  uncross: (sourceDocumentId: string) => Promise<void>;
  refresh: () => void;
  clearError: () => void;
}

function useCrossDocumentsInternal({
  config,
  documentId,
  docState,
  crossingFetch,
  canCross = true,
  onCrossingChanged,
}: UseCrossDocumentsOptions): { state: CrossDocumentsState; actions: CrossDocumentsActions } {
  // Traduce los errores que genera el kit; los que trae el backend pasan intactos.
  const { t } = useMasterDocumentTranslation();
  const [available, setAvailable] = useState<AvailableSourceDocument[]>([]);
  const [crossed, setCrossed] = useState<CrossedSourceDocument[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const routes = useMemo(
    () => ({ ...DEFAULT_ROUTES, ...(config?.routes ?? {}) }),
    [config?.routes],
  );

  const editable = canCross && !!config && (config.crossOn?.includes(docState) ?? false);

  // ── Fetch de disponibles + cruzados ──────────────────────
  const fetchAll = useCallback(() => {
    if (!config || !config.enabled || !documentId) return;
    setLoading(true);
    setError(undefined);
    Promise.allSettled([
      crossingFetch<unknown>('GET', buildRoute(routes.available, documentId)),
      crossingFetch<unknown>('GET', buildRoute(routes.crossed, documentId)),
    ])
      .then(([availRes, crossedRes]) => {
        if (availRes.status === 'fulfilled') {
          setAvailable(toArray(availRes.value, 'documents').map(mapAvailable));
        } else {
          // Falla de disponibles SÍ es relevante para el usuario (no puede cruzar).
          setError(resolveCrossingError(availRes.reason, t).message);
        }
        // El endpoint de cruzados puede no existir todavía (degradación suave): si
        // falla, dejamos la lista vacía sin romper el panel de disponibles.
        if (crossedRes.status === 'fulfilled') {
          setCrossed(toArray(crossedRes.value, 'documents').map(mapCrossed));
        }
      })
      .finally(() => setLoading(false));
  }, [config, documentId, crossingFetch, routes.available, routes.crossed, t]);

  useEffect(() => {
    if (!config || !config.enabled || !documentId) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, config]);

  // ── Selección whole-document ─────────────────────────────
  const toggleSelect = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) =>
      selected ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id),
    );
  }, []);

  // ── Subtotal corrido Σ(ECD.net) − Σ(DCD.net) ─────────────
  // Los montos viajan positivos; el signo solo agrega (no se remultiplica).
  const subtotal = useMemo(() => {
    const selected = new Set(selectedIds);
    return available.reduce(
      (acc, doc) => (selected.has(doc.id) ? acc + doc.crossingSign * doc.netTotal : acc),
      0,
    );
  }, [available, selectedIds]);

  const crossedSubtotal = useMemo(
    () => crossed.reduce((acc, doc) => acc + doc.crossingSign * doc.netTotal, 0),
    [crossed],
  );

  // ── Cruzar (todo-o-nada sobre la selección) ──────────────
  const cross = useCallback(async () => {
    if (!config || !documentId || selectedIds.length === 0) return;
    setBusy(true);
    setError(undefined);
    try {
      await crossingFetch('POST', buildRoute(routes.cross, documentId), {
        source_document_ids: selectedIds,
      });
      setSelectedIds([]);
      fetchAll();
      onCrossingChanged?.();
    } catch (err) {
      setError(resolveCrossingError(err, t).message);
    } finally {
      setBusy(false);
    }
  }, [config, documentId, selectedIds, crossingFetch, routes.cross, fetchAll, onCrossingChanged, t]);

  // ── Desvincular un documento ya cruzado ──────────────────
  const uncross = useCallback(
    async (sourceDocumentId: string) => {
      if (!config || !documentId) return;
      setBusy(true);
      setError(undefined);
      try {
        await crossingFetch('POST', buildRoute(routes.uncross, documentId), {
          source_document_id: sourceDocumentId,
        });
        fetchAll();
        onCrossingChanged?.();
      } catch (err) {
        setError(resolveCrossingError(err, t).message);
      } finally {
        setBusy(false);
      }
    },
    [config, documentId, crossingFetch, routes.uncross, fetchAll, onCrossingChanged, t],
  );

  const clearError = useCallback(() => setError(undefined), []);

  const state: CrossDocumentsState = {
    available,
    crossed,
    selectedIds,
    subtotal,
    crossedSubtotal,
    loading,
    busy,
    editable,
    error,
  };

  const actions: CrossDocumentsActions = {
    toggleSelect,
    cross,
    uncross,
    refresh: fetchAll,
    clearError,
  };

  return { state, actions };
}

/**
 * Motor de cruce WHOLE-DOCUMENT (Épica 105-5). Devuelve `null` cuando
 * `config` está ausente o deshabilitado. Mantiene la misma forma que
 * {@link useCrossing} (per-línea) pero con el modelo documento-entero:
 * selección todo-o-nada, signo +/−, subtotal `Σ(ECD)−Σ(DCD)`, cruzar/desvincular.
 */
export function useCrossDocuments(
  options: UseCrossDocumentsOptions,
): { state: CrossDocumentsState; actions: CrossDocumentsActions } | null {
  const result = useCrossDocumentsInternal(options);
  if (!options.config || !options.config.enabled) return null;
  return result;
}
